/* eslint-disable max-lines-per-function */
import type { Variants } from "framer-motion";
import { useCallback, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import { getExitDuration } from "../transitions/pageTransitions";

interface PreloadOptions {
  /** Function to call during exit animation to preload data */
  preload?: () => Promise<unknown> | void;
}

interface UseTransitionNavigationOptions {
  /** Default transition variant to use when none specified in transitionTo() */
  defaultVariant?: Variants;
  /** Manual duration in milliseconds to wait before navigation (used if no variant provided) */
  delay?: number;
  /** Called when transition starts */
  onTransitionStart?: () => void;
  /** Called when navigation completes */
  onNavigationComplete?: () => void;
  /** Disable View Transitions API (useful for debugging) */
  disableViewTransitions?: boolean;
}

interface UseTransitionNavigationReturn {
  /** Whether any exit transition is currently active */
  isTransitioning: boolean;
  /** Navigate with delayed transition - can override variant per call and add preloading */
  transitionTo: (path: string, variant?: Variants, options?: PreloadOptions) => void;
  /** Navigate immediately without transition delay */
  navigateImmediately: (path: string) => void;
  /** Whether browser supports View Transitions API */
  supportsViewTransitions: boolean;
}

// Minimal runtime-friendly type for the ViewTransition returned by
// document.startViewTransition. We only care about the optional `.finished` promise.
type ViewTransitionLike = {
  finished?: Promise<unknown>;
};

/**
 * Check if browser supports View Transitions API
 * Chrome 111+, Edge 111+, Safari 18+, Firefox 144+
 */
function hasViewTransitionSupport(): boolean {
  if (typeof document === "undefined") return false;
  return "startViewTransition" in document;
}

/**
 * Custom hook for managing page transitions with delayed navigation.
 *
 * This allows exit animations to complete before navigation occurs,
 * creating smooth cinematic transitions between pages.
 *
 * @example
 * ```tsx
 * function MyPage() {
 *   const { isTransitioning, transitionTo } = useTransitionNavigation({
 *     defaultVariant: pageTransitions.slideHorizontal // Optional default
 *   });
 *
 *   const handleMoreInfo = () => {
 *     transitionTo('/home/title/movie-slug', pageTransitions.fullScreenFade);
 *   };
 *
 *   const handlePlay = () => {
 *     transitionTo('/home/watch/movie-slug', pageTransitions.cinematicFade);
 *   };
 *
 *   return (
 *     <motion.div
 *       animate={isTransitioning ? "exit" : "animate"}
 *       variants={getCurrentVariant()} // You'll need to track current variant
 *     >
 *       <button onClick={handleMoreInfo}>More Info</button>
 *       <button onClick={handlePlay}>Play</button>
 *     </motion.div>
 *   );
 * }
 * ```
 */
/**
 * useTransitionNavigation Hook (Enhanced with View Transitions API)
 *
 * A flexible hook for managing page transitions with browser-native View Transitions
 * support and Framer Motion fallback. Each navigation can specify its own transition
 * type for contextually appropriate animations.
 *
 * **View Transitions API Integration:**
 * - Supported in Chrome 111+, Edge 111+, Safari 18+, Firefox 144+
 * - Wraps navigation in `document.startViewTransition()` when available
 * - Provides smooth browser-native crossfades and shared element animations
 * - Mapbox background stays visually stable (via `view-transition-name: map-background`)
 * - Content slides/fades (via `view-transition-name: content`)
 * - Graceful fallback to Framer Motion-only in older browsers
 *
 * @example
 * ```tsx
 * const { transitionTo, supportsViewTransitions } = useTransitionNavigation({
 *   defaultVariant: pageTransitions.slideHorizontal
 * });
 *
 * console.log('Using View Transitions:', supportsViewTransitions); // true in modern browsers
 *
 * const handleMoreInfo = () => {
 *   transitionTo('/title/details', pageTransitions.fullScreenFade); // 600ms
 * };
 * ```
 */
export function useTransitionNavigation(options: UseTransitionNavigationOptions = {}): UseTransitionNavigationReturn {
  const {
    defaultVariant,
    delay: manualDelay,
    onTransitionStart,
    onNavigationComplete,
    disableViewTransitions = false,
  } = options;

  const navigate = useNavigate();
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Use ref to track transitioning state without causing useCallback recreation
  const isTransitioningRef = useRef(false);

  const supportsViewTransitions = hasViewTransitionSupport() && !disableViewTransitions;

  const transitionTo = useCallback(
    (path: string, variant?: Variants, preloadOptions?: PreloadOptions) => {
      // Check ref instead of state to avoid stale closures
      if (isTransitioningRef.current) return;

      isTransitioningRef.current = true;
      setIsTransitioning(true);
      onTransitionStart?.();

      // Handle async preloading + navigation

      void (async () => {
        // Start both preload and exit animation in parallel
        const startTime = Date.now();
        const minTransitionDuration = 500; // Minimum time to show exit animation (ms)

        // Start preloading
        let preloadPromise: Promise<void> = Promise.resolve();
        if (preloadOptions?.preload) {
          console.log("🚀 Preloading data during exit animation...");
          const result = preloadOptions.preload();
          if (result) {
            preloadPromise = result
              .then(() => console.log("✅ Preload complete"))
              .catch((err: unknown) => {
                console.warn("⚠️ Preload failed (non-fatal):", err);
              });
          }
        }

        // Wait for preload to complete
        await preloadPromise;

        // Calculate how much time has elapsed
        const elapsed = Date.now() - startTime;
        const remainingTime = Math.max(0, minTransitionDuration - elapsed);

        // Wait for remaining transition time if preload finished early
        if (remainingTime > 0) {
          console.log(`⏱️ Preload finished in ${elapsed}ms, waiting ${remainingTime}ms for animation`);
          await new Promise((resolve) => setTimeout(resolve, remainingTime));
        }

        // Now navigate with View Transition (data is ready, animation had time to play)
        if (supportsViewTransitions && "startViewTransition" in document) {
          const docWithVT = document as unknown as {
            startViewTransition: (cb?: () => void) => ViewTransitionLike;
          };

          const vt = docWithVT.startViewTransition(() => {
            // prettier-ignore
            flushSync(() => navigate(path)); // NOSONAR
          });

          const finished = vt?.finished;
          if (finished) {
            finished
              .then(() => {
                isTransitioningRef.current = false;
                onNavigationComplete?.();
              })
              .catch(() => {
                isTransitioningRef.current = false;
                onNavigationComplete?.();
              });
          } else {
            setTimeout(() => {
              isTransitioningRef.current = false;
              onNavigationComplete?.();
            }, 350);
          }
        } else {
          // Fallback: delayed navigation
          const effectiveVariant = variant || defaultVariant;
          const delay = effectiveVariant ? getExitDuration(effectiveVariant) : (manualDelay ?? 1200);

          setTimeout(() => {
            navigate(path);
            onNavigationComplete?.();
          }, delay);
        }
      })();
    },
    [navigate, defaultVariant, manualDelay, onTransitionStart, onNavigationComplete, supportsViewTransitions],
  );

  const navigateImmediately = useCallback(
    (path: string) => {
      if (supportsViewTransitions && "startViewTransition" in document) {
        const docWithVT = document as unknown as {
          startViewTransition: (cb?: () => void) => ViewTransitionLike;
        };

        const vt = docWithVT.startViewTransition(() => {
          flushSync(() => {
            navigate(path);
          });
        });
        const finished: Promise<unknown> | undefined = vt?.finished;
        if (finished && typeof finished.then === "function") {
          finished.then(() => onNavigationComplete?.()).catch(() => onNavigationComplete?.());
        } else {
          setTimeout(() => onNavigationComplete?.(), 350);
        }
      } else {
        navigate(path);
        onNavigationComplete?.();
      }
    },
    [navigate, onNavigationComplete, supportsViewTransitions],
  );

  return {
    isTransitioning,
    transitionTo,
    navigateImmediately,
    supportsViewTransitions,
  };
}
